import { currentLoggedUserVar } from '@/shared/api';
import type { GetUserCompaniesResponse } from '@/shared/api/graphql/company';
import { GET_USER_COMPANIES } from '@/shared/api/graphql/company';
import { ROUTES } from '@/shared/config';
import useToken from '@/shared/lib/hooks/useToken';
import { Role } from '@/shared/lib/types/admin';
import { useSwitchCompanyStore } from '@/shared/model';
import { color } from '@/shared/styles/assets/colors';
import Button from '@/shared/ui/Button';
import { Body1 } from '@/shared/ui/components/Typography';
import type { BaseDialogProps } from '@/shared/ui/DialogKit';
import { BaseDialog, ConfirmDialog } from '@/shared/ui/DialogKit';
import TruentityTextField from '@/shared/ui/TruentityTextField';
import { useQuery, useReactiveVar } from '@apollo/client';
import { Search } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  DialogContent,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Stack
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = BaseDialogProps & {
  hideDialog: () => void;
};

const SwitchCompaniesDialog = ({ hideDialog, ...props }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();
  const { setCurrentCompany, setNewCompany } = useSwitchCompanyStore();
  const currentUser = useReactiveVar(currentLoggedUserVar);
  const navigate = useNavigate();
  const { roleType } = useToken();
  const canSeeMockCompanies = roleType === Role.SUPER || roleType === Role.ADMIN;

  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: userCompanies,
    loading: userCompaniesLoading,
    error: userCompaniesError
  } = useQuery<GetUserCompaniesResponse>(GET_USER_COMPANIES, {
    variables: {
      hideCurrent: true
    },
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    if (userCompaniesError) {
      enqueueSnackbar(userCompaniesError.message ?? 'Could not load User Pharmacies', { variant: 'error' });
    }
  }, [enqueueSnackbar, userCompaniesError]);

  // Mock/test companies are only switchable by super admins.
  const rawCompanies = (userCompanies?.getUserRelyingParties?.relyingParties ?? []).filter(
    company => canSeeMockCompanies || !company?.isMock
  );
  const hasCompanies = rawCompanies.length > 0;

  const filteredCompanies = rawCompanies.filter(company => company?.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())) ?? [];

  const handleSwitchCompany = ({ companyId, companyName }: { companyId: string; companyName: string }) => {
    const modal = showModal(ConfirmDialog, {
      title: 'Confirm New Login',
      message: `This will sign you out of <b>${currentUser?.relyingParty?.name ?? 'current pharmacy'}</b> and sign you in to <b>${
        companyName ?? 'new pharmacy'
      }</b>. Do you wish to proceed?`,
      onAgree: () => {
        // navigate to intermediate screen
        setNewCompany({
          id: companyId,
          name: companyName
        });
        setCurrentCompany({
          id: currentUser?.relyingParty?.id ?? '',
          name: currentUser?.relyingParty?.name ?? ''
        });
        hideDialog();
        modal.hide();
        navigate(ROUTES.SWITCH_COMPANY);
      },
      onDisagree: () => {
        modal.hide();
      }
    });
  };

  return (
    <BaseDialog {...props} hideDialog={hideDialog} fullWidth maxWidth={'sm'}>
      <DialogContent>
        <Stack
          sx={{
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            rowGap: 1
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-start'
            }}
          >
            Select a Pharmacy to Sign In
          </Stack>
          {userCompaniesLoading ? (
            <Stack
              direction={'column'}
              sx={{
                justifyContent: 'center',
                width: '100%',
                alignItems: 'center'
              }}
            >
              <CircularProgress
                size={20}
                sx={{
                  margin: 2
                }}
              />
              <Body1> Loading pharmacies for user </Body1>
            </Stack>
          ) : (
            <Stack
              direction={'column'}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'center',
                minWidth: 360,
                alignSelf: 'center',
                padding: 2
              }}
            >
              <TruentityTextField
                label="Search Pharmacies"
                fullWidth
                value={searchTerm}
                disabled={!hasCompanies}
                onChange={e => setSearchTerm(e.target.value)}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  )
                }}
              />
              <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden', background: color.white, mt: 0.5 }}>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List>
                    {filteredCompanies.map(company => (
                      <ListItem disablePadding key={company?.id}>
                        <ListItemButton
                          onClick={() => {
                            handleSwitchCompany({
                              companyId: company?.id,
                              companyName: company?.name
                            });
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar alt={company?.name} src={company?.logoUrl} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                  alignItems: 'center'
                                }}
                              >
                                <span>{company?.name}</span>
                                {company?.isMock && <Chip label="Mock" size="small" color="warning" variant="outlined" />}
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {filteredCompanies.length === 0 && (
                      <ListItem>
                        <ListItemText
                          primary={
                            !hasCompanies ? 'You are not assigned to any other pharmacies.' : 'No pharmacies found matching your search.'
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Paper>
            </Stack>
          )}
          <Stack
            direction="row"
            sx={{
              alignContent: 'center',
              justifyContent: 'flex-end'
            }}
          >
            <Button
              type="reset"
              a11yLabel="Cancel"
              appearance="outline"
              onClick={() => {
                hideDialog();
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </BaseDialog>
  );
};

export default SwitchCompaniesDialog;
